import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { partition } from 'remeda';
import { TIME_FORMAT } from 'src/constants/date';
import {
  GET_SIGNAL_BUSINESS_HOURS_QUERY,
  UPDATE_SIGNAL_BUSINESS_HOURS_QUERY,
} from 'src/graphql/entities/signal-business-hours/signal-business-hours.operations';
import {
  SignalBusinessHoursMutationInput,
  SignalBusinessHoursQueryResponse,
} from 'src/graphql/entities/signal-business-hours/signal-business-hours.types';
import { ConditionInput, QueryVariables } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { GetSignalBusinessHoursQuery } from './use-signal-business-hours';

export interface UpdateSignalBusinessHoursMutation {
  updateSignalBusinessHours: SignalBusinessHoursQueryResponse[];
}

export type UpdateSignalBusinessHoursMutationParams = {
  conditions: ConditionInput[];
  data: Partial<SignalBusinessHoursMutationInput>;
};

export const useUpdateSignalBusinessHours = () => {
  const { showErrorAlert } = useNotifications();
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const [updateSignalBusinessHoursMutation, { loading: isUpdating }] = useMutation<
    UpdateSignalBusinessHoursMutation,
    UpdateSignalBusinessHoursMutationParams
  >(UPDATE_SIGNAL_BUSINESS_HOURS_QUERY, {
    update(cache, { data }) {
      if (!data?.updateSignalBusinessHours?.length) {
        return;
      }

      cache.updateQuery<GetSignalBusinessHoursQuery, QueryVariables>(
        {
          query: GET_SIGNAL_BUSINESS_HOURS_QUERY,
          variables: {
            params: {
              filters: [
                {
                  attribute: 'partnerId',
                  operator: '=',
                  value: partnerId?.toString()!,
                },
              ],
            },
          },
        },
        (cached) => {
          const updatedSignalBusinessHoursIds = data.updateSignalBusinessHours.map(
            (item) => item.id,
          );
          const [updatedSignalBusinessHours, restSignalBusinessHours] = partition(
            cached?.getSignalBusinessHours.records || [],
            (item) => updatedSignalBusinessHoursIds.includes(item.id),
          );

          const updatedValuesById = data?.updateSignalBusinessHours.reduce<
            Record<string, SignalBusinessHoursQueryResponse>
          >(
            (acc, item) => ({
              ...acc,
              [item.id]: item,
            }),
            {},
          );

          const records = updatedSignalBusinessHours.map<SignalBusinessHoursQueryResponse>(
            (record) => ({
              ...record,
              ...updatedValuesById[record.id],
            }),
          );

          return {
            getSignalBusinessHours: {
              __typename: 'PartnerHolidayDataResponse',
              records: [...restSignalBusinessHours, ...records],
              metadata: cached?.getSignalBusinessHours.metadata ?? {},
            },
          };
        },
      );
    },
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to update Signal Business Hours, please try again');
    },
  });

  const updateSignalBusinessHours = useCallback(
    async (id: string, inputField: 'timeFrom' | 'timeTo', value: dayjs.Dayjs) => {
      if (partnerId === null) {
        console.error('Cannot update Signal business hours. Current PartnerId is not found');
        return;
      }
      await updateSignalBusinessHoursMutation({
        variables: {
          conditions: [
            {
              attribute: 'id',
              operator: '=',
              value: String(id),
            },
          ],
          data: {
            [inputField]: value.format(TIME_FORMAT),
          },
        },
      });
    },
    [partnerId, updateSignalBusinessHoursMutation],
  );

  return {
    updateSignalBusinessHours,
    isUpdating,
  };
};
